import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { Module } from '@api/models/UserService/Contract/module';
import { WINDOW } from '@core/app/window.token';
import { SeoPageModel } from '@core/seo/seo.model';


/**
 * Separator between the page title and the module name in the document title
 */
export const TITLE_SEPARATOR: string = ' — ';

@Injectable({
    providedIn: 'root',
})
export class SeoService {
    readonly #isInit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    readonly #window: Window = inject(WINDOW);
    readonly #title: Title = inject(Title);

    #moduleName: string = null;
    #pageTitle: string = null;
    #subPageTitle: string = null;
    #isDynamicTitle: boolean = false;

    public get loaded(): Observable<boolean> {
        return this.#isInit.asObservable();
    }

    /**
     * Title was set by a component during the current navigation (see setMeta) and must not be reset by the route title
     */
    public get isDynamicTitle(): boolean {
        return this.#isDynamicTitle;
    }

    /**
     * Forgets the component-set title flag; called on navigation start.
     * SeoService must not inject Router: the Router depends on TitleStrategy, which depends on this service.
     */
    public resetDynamicTitle(): void {
        this.#isDynamicTitle = false;
    }

    /**
     * Current document title
     */
    public get title(): string {
        return this.#title.getTitle();
    }

    public init(menuItems: Module[]): void {
        this.#moduleName = this.getRootSeoData(menuItems)?.title ?? null;
        this.applyTitle();
        this.#isInit.next(true);
    }

    /**
     * Sets the page part of the document title; null resets it to the module name only
     */
    public setPageTitle(pageTitle: string | null): void {
        this.#pageTitle = pageTitle || null;
        this.applyTitle();
    }

    /**
     * Sub page (third level menu item) part of the document title
     */
    public get subPageTitle(): string {
        return this.#subPageTitle;
    }

    /**
     * Sets the sub page part, shown before the page title; null removes it
     */
    public setSubPageTitle(subPageTitle: string | null): void {
        this.#subPageTitle = subPageTitle || null;
        this.applyTitle();
    }

    /**
     * Sets a title known only to a component (e.g. report name)
     */
    public setMeta(meta: SeoPageModel): void {
        if (meta?.title) {
            this.#isDynamicTitle = true;
            this.setPageTitle(meta.title);
        }
    }

    private applyTitle(): void {
        const parts: string[] = [this.#subPageTitle, this.#pageTitle, this.#moduleName]
            .filter(Boolean)
            .filter((part: string, index: number, all: string[]) => part !== all[index - 1]);

        if (parts.length) {
            this.#title.setTitle(parts.join(TITLE_SEPARATOR));
        }
    }

    private getRootSeoData(menuItems: Module[]): SeoPageModel {
        const currentModule: Module = environment.isLocalhost
            ? menuItems.find(this.getModuleByPort.bind(this))
            : menuItems.find(this.getModuleByPathName.bind(this));

        return currentModule
            ? {
                title: currentModule?.mdName,
            }
            : null;
    }

    private getModuleByPathName(item: Module): boolean {
        return item.url === this.#window.location.hostname;
    }

    private getModuleByPort(item: Module): boolean {
        switch (Number(this.#window.location.port)) {
            case 4200:
                return item.mdId === 101;
            case 4202:
                return item.mdId === 102;
            case 4208:
                return item.mdId === 103;
            case 4210:
                return item.mdId === 104;
            case 4203:
                return item.mdId === 105;
            case 4204:
                return item.mdId === 106;
            case 4205:
                return item.mdId === 107;
            case 4206:
                return item.mdId === 108;
            case 4207:
                return item.mdId === 109;
            case 4209:
                return item.mdId === 110;
            case 4211:
                return item.mdId === 111;
        }
    }
}
